import React, { useState } from 'react';
import { Calculator } from 'lucide-react';
import { SEO } from '../components/SEO';

interface ScreedState {
  length: string;
  width: string;
  thickness: string;
  screedType: 'cement' | 'anhydrite';
  hasInsulation: boolean;
  hasFloorHeating: boolean;
}

interface ScreedResult {
  volume: number;
  cementAmount: number;
  sandAmount: number;
  waterAmount: number;
  additiveAmount: number;
  coverage: number;
}

function KalkulatorEstriha() {
  const [values, setValues] = useState<ScreedState>({
    length: '',
    width: '',
    thickness: '',
    screedType: 'cement',
    hasInsulation: false,
    hasFloorHeating: false
  });

  const [results, setResults] = useState<ScreedResult | null>(null);

  const calculateMaterials = () => {
    const length = parseFloat(values.length);
    const width = parseFloat(values.width);
    let thickness = parseFloat(values.thickness);

    if (isNaN(length) || isNaN(width) || isNaN(thickness)) {
      return null;
    }

    // Calculate volume in cubic meters
    const volume = (length * width * thickness) / 1000; // Convert mm to m for thickness

    let cementAmount, sandAmount, waterAmount, additiveAmount;

    if (values.screedType === 'cement') {
      // Cement screed mix ratio (1:4)
      cementAmount = volume * 350; // kg/m³
      sandAmount = volume * 1600; // kg/m³
      waterAmount = volume * 175; // liters/m³
      additiveAmount = cementAmount * 0.01; // 1% of cement weight
    } else {
      // Anhydrite screed
      cementAmount = volume * 600; // kg/m³ (anhydrite binder)
      sandAmount = volume * 1200; // kg/m³
      waterAmount = volume * 120; // liters/m³
      additiveAmount = volume * 3; // kg/m³ (flow improver)
    }

    // Add extra material for floor heating if needed
    if (values.hasFloorHeating) {
      thickness += 20; // Additional 20mm for heating pipes
    }

    // Coverage in m²
    const coverage = length * width;

    return {
      volume: parseFloat(volume.toFixed(2)),
      cementAmount: parseFloat(cementAmount.toFixed(2)),
      sandAmount: parseFloat(sandAmount.toFixed(2)),
      waterAmount: parseFloat(waterAmount.toFixed(2)),
      additiveAmount: parseFloat(additiveAmount.toFixed(2)),
      coverage: parseFloat(coverage.toFixed(2))
    };
  };

  const handleInputChange = (field: keyof ScreedState, value: string | boolean) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCalculate = () => {
    const results = calculateMaterials();
    setResults(results);
  };

  const getMinimumThickness = (): number => {
    let minThickness = 40; // Base minimum thickness

    if (values.hasFloorHeating) {
      minThickness = 45; // Minimum for floor heating
    }

    if (values.hasInsulation) {
      minThickness += 10; // Additional thickness for insulation
    }

    return minThickness;
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Estriha",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte potrebnu količinu materijala za izradu estriha. Uključuje izračun za cementni i anhidritni estrih s opcijama za podno grijanje.",
    "url": "https://online-kalkulator.com/kalkulator-estriha",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    }
  };

  return (
    <>
      <SEO 
        title="Kalkulator Estriha | Izračun Materijala za Estrih"
        description="Izračunajte potrebnu količinu materijala za izradu estriha. Uključuje izračun za cementni i anhidritni estrih s opcijama za podno grijanje."
        canonicalUrl="/kalkulator-estriha"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calculator className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Estriha</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Dimensions */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dužina (m)
                  </label>
                  <input
                    type="number"
                    value={values.length}
                    onChange={(e) => handleInputChange('length', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 5"
                    step="0.1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Širina (m)
                  </label>
                  <input
                    type="number"
                    value={values.width}
                    onChange={(e) => handleInputChange('width', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 4"
                    step="0.1"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Debljina (mm)
                  </label>
                  <input
                    type="number"
                    value={values.thickness}
                    onChange={(e) => handleInputChange('thickness', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Min. ${getMinimumThickness()} mm`}
                    min={getMinimumThickness()}
                  />
                </div>
              </div>

              {/* Options */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vrsta estriha
                  </label>
                  <select
                    value={values.screedType}
                    onChange={(e) => handleInputChange('screedType', e.target.value as 'cement' | 'anhydrite')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="cement">Cementni estrih</option>
                    <option value="anhydrite">Anhidritni estrih</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={values.hasInsulation}
                      onChange={(e) => handleInputChange('hasInsulation', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      Toplinska izolacija ispod estriha
                    </span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={values.hasFloorHeating}
                      onChange={(e) => handleInputChange('hasFloorHeating', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      Podno grijanje
                    </span>
                  </label>
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Izračunaj
              </button>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 bg-blue-50 p-4 rounded-lg text-center">
                      <div className="text-sm text-gray-600 mb-1">Površina</div>
                      <div className="text-xl font-bold text-blue-600">{results.coverage} m²</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-sm text-gray-600 mb-1">Volumen</div>
                      <div className="text-lg font-semibold">{results.volume} m³</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-sm text-gray-600 mb-1">
                        {values.screedType === 'cement' ? 'Cement' : 'Anhidritno vezivo'}
                      </div>
                      <div className="text-lg font-semibold">{results.cementAmount} kg</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-sm text-gray-600 mb-1">Pijesak</div>
                      <div className="text-lg font-semibold">{results.sandAmount} kg</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-sm text-gray-600 mb-1">Voda</div>
                      <div className="text-lg font-semibold">{results.waterAmount} L</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-sm text-gray-600 mb-1">Aditivi</div>
                      <div className="text-lg font-semibold">{results.additiveAmount} kg</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Estriha</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator estriha pomaže vam izračunati potrebne količine materijala za izradu
                  cementnog ili anhidritnog estriha. Uzima u obzir dimenzije prostora, vrstu estriha,
                  te posebne zahtjeve poput podnog grijanja ili toplinske izolacije.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Vrste Estriha</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Cementni estrih</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Tradicionalni i najčešće korišteni tip</li>
                      <li>Omjer miješanja cement:pijesak = 1:4</li>
                      <li>Brže sušenje od anhidritnog</li>
                      <li>Pogodno za sve vrste podnih obloga</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Anhidritni estrih</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Samorazlijevajući estrih</li>
                      <li>Bolja toplinska provodljivost</li>
                      <li>Idealan za podno grijanje</li>
                      <li>Manja mogućnost pucanja</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Minimalne Debljine</h2>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="font-medium">Standardni estrih</span>
                    <span>40 mm</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="font-medium">S podnim grijanjem</span>
                    <span>45 mm iznad cijevi</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="font-medium">Na izolaciji</span>
                    <span>+10 mm na osnovnu debljinu</span>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Priprema i Ugradnja</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Priprema podloge</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Očistite podlogu od prašine i nečistoća</li>
                      <li>Postavite rubne trake uz zidove</li>
                      <li>Provjerite ravnost podloge</li>
                      <li>Postavite hidroizolaciju ako je potrebno</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Ugradnja estriha</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Pravilno miješanje komponenti</li>
                      <li>Ravnomjerno nanošenje</li>
                      <li>Pravilno zbijanje</li>
                      <li>Zaglađivanje površine</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračun je informativan i može odstupati od stvarnih potreba</li>
                  <li>Uvijek dodajte 10% na izračunatu količinu za rastur</li>
                  <li>Vrijeme sušenja ovisi o:
                    <ul className="list-none pl-6 pt-2 space-y-1">
                      <li>- Debljini estriha</li>
                      <li>- Vrsti estriha</li>
                      <li>- Uvjetima u prostoru</li>
                      <li>- Ventilaciji</li>
                    </ul>
                  </li>
                  <li>Za velike površine predvidite dilatacijske spojnice</li>
                  <li>Konzultirajte stručnjaka za specifične zahtjeve</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorEstriha;